import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../firebase';

const rtcConfiguration = () => {
  const iceServers = [
    { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
  ];

  const turnUrl = process.env.REACT_APP_WEBRTC_TURN_URL;
  if (turnUrl) {
    iceServers.push({
      urls: turnUrl.split(',').map((value) => value.trim()).filter(Boolean),
      username: process.env.REACT_APP_WEBRTC_TURN_USERNAME || '',
      credential: process.env.REACT_APP_WEBRTC_TURN_CREDENTIAL || '',
    });
  }

  return { iceServers, iceCandidatePoolSize: 10 };
};

const randomConnectionId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const descriptionData = (description) => ({
  type: description.type,
  sdp: description.sdp,
});

const candidateData = (candidate) => {
  if (typeof candidate.toJSON === 'function') return candidate.toJSON();
  return {
    candidate: candidate.candidate,
    sdpMid: candidate.sdpMid,
    sdpMLineIndex: candidate.sdpMLineIndex,
    usernameFragment: candidate.usernameFragment,
  };
};

const statusLabel = (state) => {
  if (state === 'connected') return 'Connected. Audio and video are live.';
  if (state === 'connecting') return 'Connecting securely...';
  if (state === 'disconnected') return 'Connection interrupted. Trying to reconnect...';
  if (state === 'failed') return 'The live connection failed. Check your network and try again.';
  if (state === 'closed') return 'Call ended.';
  return 'Waiting for the other participant...';
};

function createPeer({
  localStream,
  generation,
  candidateTarget,
  onRemoteStream,
  onStatus,
  onError,
}) {
  const peer = new RTCPeerConnection(rtcConfiguration());
  const remoteStream = new MediaStream();
  const pendingCandidates = [];
  let remoteDescriptionReady = false;

  localStream.getTracks().forEach((track) => peer.addTrack(track, localStream));

  peer.ontrack = (event) => {
    const tracks = event.streams?.[0]?.getTracks?.() || [event.track];
    tracks.forEach((track) => {
      if (!remoteStream.getTracks().some((item) => item.id === track.id)) {
        remoteStream.addTrack(track);
      }
    });
    onRemoteStream(remoteStream);
  };

  peer.onconnectionstatechange = () => onStatus(statusLabel(peer.connectionState));
  peer.oniceconnectionstatechange = () => {
    if (peer.iceConnectionState === 'failed') peer.restartIce?.();
  };
  peer.onicecandidate = async (event) => {
    if (!event.candidate) return;
    try {
      await addDoc(candidateTarget, {
        generation,
        candidate: candidateData(event.candidate),
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      onError(error);
    }
  };

  const addRemoteCandidate = async (candidate) => {
    if (!remoteDescriptionReady) {
      pendingCandidates.push(candidate);
      return;
    }
    try {
      await peer.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      onError(error);
    }
  };

  const setRemoteDescription = async (description) => {
    await peer.setRemoteDescription(new RTCSessionDescription(description));
    remoteDescriptionReady = true;
    while (pendingCandidates.length) {
      await addRemoteCandidate(pendingCandidates.shift());
    }
  };

  return { peer, addRemoteCandidate, setRemoteDescription };
}

export async function connectSessionRoom({
  sessionId,
  userId,
  isInitiator,
  localStream,
  onRemoteStream,
  onStatus,
  onError,
}) {
  if (!window.RTCPeerConnection) {
    throw new Error('This browser does not support secure WebRTC calls.');
  }

  const signalRef = doc(db, 'sessions', sessionId, 'roomConnection', 'main');
  const callerCandidates = collection(signalRef, 'callerCandidates');
  const calleeCandidates = collection(signalRef, 'calleeCandidates');
  const unsubscribers = [];
  let activePeer = null;
  let activeGeneration = '';
  let handledAnswer = false;

  const reportError = (error) => {
    console.error('WebRTC room error:', error);
    onError(error);
  };

  const closePeer = () => {
    if (!activePeer) return;
    activePeer.peer.ontrack = null;
    activePeer.peer.onicecandidate = null;
    activePeer.peer.onconnectionstatechange = null;
    activePeer.peer.close();
    activePeer = null;
  };

  const listenForCandidates = (candidateCollection, generation, peerRoom) => {
    const seen = new Set();
    return onSnapshot(candidateCollection, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type !== 'added' || seen.has(change.doc.id)) return;
        const data = change.doc.data();
        if (data.generation !== generation || !data.candidate) return;
        seen.add(change.doc.id);
        peerRoom.addRemoteCandidate(data.candidate);
      });
    }, reportError);
  };

  if (isInitiator) {
    activeGeneration = randomConnectionId();
    activePeer = createPeer({
      localStream,
      generation: activeGeneration,
      candidateTarget: callerCandidates,
      onRemoteStream,
      onStatus,
      onError: reportError,
    });
    unsubscribers.push(listenForCandidates(calleeCandidates, activeGeneration, activePeer));
    unsubscribers.push(onSnapshot(signalRef, async (snapshot) => {
      const data = snapshot.data();
      if (!data?.answer || data.generation !== activeGeneration || handledAnswer) return;
      handledAnswer = true;
      try {
        await activePeer.setRemoteDescription(data.answer);
      } catch (error) {
        handledAnswer = false;
        reportError(error);
      }
    }, reportError));

    const offer = await activePeer.peer.createOffer();
    await activePeer.peer.setLocalDescription(offer);
    await setDoc(signalRef, {
      generation: activeGeneration,
      callerId: userId,
      offer: descriptionData(offer),
      answer: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    onStatus('Room opened. Waiting for the client...');
  } else {
    onStatus('Waiting for the reader to open the live connection...');
    let candidateUnsubscribe = null;
    unsubscribers.push(onSnapshot(signalRef, async (snapshot) => {
      const data = snapshot.data();
      if (!data?.offer || !data.generation || data.generation === activeGeneration) return;

      activeGeneration = data.generation;
      candidateUnsubscribe?.();
      closePeer();
      activePeer = createPeer({
        localStream,
        generation: activeGeneration,
        candidateTarget: calleeCandidates,
        onRemoteStream,
        onStatus,
        onError: reportError,
      });
      candidateUnsubscribe = listenForCandidates(callerCandidates, activeGeneration, activePeer);

      try {
        await activePeer.setRemoteDescription(data.offer);
        const answer = await activePeer.peer.createAnswer();
        await activePeer.peer.setLocalDescription(answer);
        await setDoc(signalRef, {
          generation: activeGeneration,
          answererId: userId,
          answer: descriptionData(answer),
          updatedAt: serverTimestamp(),
        }, { merge: true });
        onStatus('Joining the reader...');
      } catch (error) {
        reportError(error);
      }
    }, reportError));
    unsubscribers.push(() => candidateUnsubscribe?.());
  }

  return () => {
    unsubscribers.forEach((unsubscribe) => unsubscribe?.());
    closePeer();
  };
}
